/**
 * v3.tasks — /v3/tasks/* (5 routes). Entity stores Title-Case status.
 */

import type {
  RequestOptions,
  ApiResult,
  GenericResult,
  TaskRecordDto,
  CreateTaskPayload,
  TaskPatchInput,
  TaskCompleteInput,
  TaskReassignInput,
  TaskListFilters,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface TasksListResponse {
  success: boolean;
  count: number;
  top?: number;
  tasks: ReadonlyArray<TaskRecordDto>;
}

export interface TaskSingleResponse {
  success: boolean;
  task?: TaskRecordDto;
  error?: string;
}

export class TasksApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(
    filters: TaskListFilters = {},
    opts?: RequestOptions
  ): Promise<TasksListResponse> {
    return this.http.get<TasksListResponse>('/v3/tasks', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public get(taskId: string, opts?: RequestOptions): Promise<TaskSingleResponse> {
    return this.http.get<TaskSingleResponse>(`/v3/tasks/${encodeURIComponent(taskId)}`, { ...opts });
  }

  public create(req: CreateTaskPayload, opts?: RequestOptions): Promise<TaskSingleResponse> {
    return this.http.post<TaskSingleResponse>('/v3/tasks', { ...opts, body: req });
  }

  public update(
    taskId: string,
    req: TaskPatchInput,
    opts?: RequestOptions
  ): Promise<TaskSingleResponse> {
    return this.http.patch<TaskSingleResponse>(`/v3/tasks/${encodeURIComponent(taskId)}`, {
      ...opts,
      body: req,
    });
  }

  public complete(
    taskId: string,
    req: TaskCompleteInput,
    opts?: RequestOptions
  ): Promise<TaskSingleResponse> {
    return this.http.post<TaskSingleResponse>(`/v3/tasks/${encodeURIComponent(taskId)}/complete`, {
      ...opts,
      body: req,
    });
  }

  public reassign(
    taskId: string,
    req: TaskReassignInput,
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.post<GenericResult>(`/v3/tasks/${encodeURIComponent(taskId)}/reassign`, {
      ...opts,
      body: req,
    });
  }
}

export type { ApiResult };
